<template>
  <div class="reserve-diff">
    <div class="bar">
      <ReceptionStatus :status="after.status" />
      <span v-if="after.receiptDatetime" class="created-at">
        受付：{{ formatDate(after.receiptDatetime, 'YYYY/MM/DD HH:mm') }}
        {{ after.inputBy ? `  (${after.inputBy})` : '' }}
      </span>
    </div>

    <dl class="reserve-info">
      <dt><CalendarIcon /></dt>
      <dd :class="{ diff: diff('reserveDatetime.date') }">
        {{ formatDate(after.reserveDatetime.date, 'YYYY/M/D (dd)') }}
      </dd>

      <dt><ClockIcon /></dt>
      <dd class="time" :class="{ diff: diff('reserveDatetime.time') || diff('utilizationTime') || diff('resetTime') }">
        <span>{{ formatTime(after.reserveDatetime.time) }} 〜 {{ formatTime(endTime) }}</span>
        <span v-if="!isBlock">（リセットタイム {{ after.resetTime }}分）</span>
      </dd>

      <template v-if="!isBlock">
        <dt><AddPeopleIcon /></dt>
        <dd :class="{ diff: diff('adults') || diff('children') }">
          <span>大人：{{ after.adults }} 名</span><span>子供：{{ after.children }} 名</span>
        </dd>
      </template>

      <dt><TableIcon /></dt>
      <dd :class="{ diff: diff('tables') }">
        <template v-if="after.tables && after.tables.length && after.tables[0].id !== -1">
          <span v-for="(table, key) in after.tables" :key="key">{{ table.name }}</span>
        </template>
        <span v-else>テーブル未指定</span>
      </dd>
    </dl>

    <div class="name">
      <em :class="{ diff: !!this.before && beforeReserveName !== afterReserveName }"> {{ afterReserveName }} </em
      >{{ isBlock ? '' : '様' }}
      <p v-if="isShowRuby" class="ruby" :class="{ diff: !!this.before && beforeRuby !== afterRuby }">{{ afterRuby }}</p>
    </div>

    <dl class="prop">
      <template v-if="after.customer.tel || after.customer.tel1 || after.customer.tel2">
        <dt><TelIcon /></dt>
        <dd :class="{ diff: diff('customer.tel') || diff('customer.tel1') || diff('customer.tel2') }">
          {{ tel(after.customer) }}
        </dd>
      </template>

      <dt><CompanyIcon /></dt>
      <dd :class="{ diff: diff('customer.officeName') }">{{ after.customer.officeName }}</dd>

      <dt><TagIcon /></dt>
      <dd :class="{ diff: diff('purpose') }">{{ purposeMap[after.purpose || 0] }}</dd>

      <template v-for="(course, i) in after.courses">
        <dt :key="`ct-${i}`"><KnifeForkIcon /></dt>
        <dd :class="{ diff: !compareToCourse(course) }" :key="`cd-${i}`">
          <div class="course">
            <span>{{ course.name }}{{ isGnavi(after.syncType) ? ` / ¥${course.price.toLocaleString()}` : '' }}</span>
            <CrossIcon width="14" height="14" />
            <span>{{ course.count }}名様</span>
          </div>
        </dd>
      </template>

      <dt><AssignIcon /></dt>
      <dd class="rsv-cstm-method" :class="{ diff: diff('type') }">{{ typeMap[after.type || 0] }}</dd>
    </dl>

    <dl class="prop">
      <dt>媒体：</dt>
      <dd :class="{ diff: diff('media') }">{{ medias.byId[after.media] }}</dd>
    </dl>
    <dl class="prop settlement">
      <dt>決済情報：</dt>
      <dd :class="{ diff: diff('settlement') }">
        {{ settlementMap[after.settlement] }}
        {{ pointPayment(after.settlement) ? `${couponPoint(after)}${couponPointNote(after)}` : '' }}
      </dd>
    </dl>
    <dl v-if="after.coupon" class="prop coupon">
      <dt>
        ぐるなびネット予約クーポン：<span :class="{ diff: diff('coupon.title') }">{{ after.coupon.title }}</span>
      </dt>
      <dd v-if="after.coupon.text" v-html="after.coupon.text"></dd>
    </dl>
    <dl class="prop">
      <dt>備考：</dt>
      <dd :class="['remarks', { diff: diff('remarks') }]">{{ after.remarks }}</dd>
    </dl>
    <dl class="prop" :class="{ 'tag-list': after.tags && after.tags.length }">
      <dt>予約タグ：</dt>
      <dd :class="{ diff: diff('tags') }">
        <TagLabels v-if="after.tags" :tags="after.tags" />
      </dd>
    </dl>
    <dl class="prop">
      <dt>ご予約名：</dt>
      <dd :class="{ diff: diff('reserveName') }">{{ after.reserveName }}</dd>
    </dl>
    <dl class="prop">
      <dt>メールアドレス：</dt>
      <dd :class="{ diff: diff('customer.mail1') || diff('customer.mail2') }">{{ mail(after.customer) }}</dd>
    </dl>
    <dl v-if="!after.receiptDatetime" class="prop">
      <dt>入力者：</dt>
      <dd>{{ after.inputBy }}</dd>
    </dl>
  </div>
</template>

<script>
import { get, isEqual } from 'lodash'
import { mapState } from 'vuex'
import { formatDate, getEndTime } from '@/utils/date'
import { reserveName, couponPoint, couponPointNote } from '@/utils/reserve'
import { formatTime } from '@/utils/time'
import typeMap, { isBlock } from '@/resources/reservation/typeMap'
import { isGnavi } from '@/resources/reservation/syncTypeMap'
import purposeMap from '@/resources/reservation/purposeMap'
import settlementMap, { pointPayment } from '@/resources/reservation/settlementMap'
import TagLabels from '@/components/molecules/TagLabels'
import ReceptionStatus from '@/components/atoms/ReceptionStatus'
import AddPeopleIcon from '@/components/icons/addPeople'
import AssignIcon from '@/components/icons/assign'
import CalendarIcon from '@/components/icons/CalendarIcon'
import ClockIcon from '@/components/icons/clock'
import CrossIcon from '@/components/icons/cross'
import CompanyIcon from '@/components/icons/company'
import KnifeForkIcon from '@/components/icons/knifeFork'
import TableIcon from '@/components/icons/table'
import TagIcon from '@/components/icons/tag'
import TelIcon from '@/components/icons/TelIcon'

export default {
  name: 'ReserveDiff',
  components: {
    AddPeopleIcon,
    AssignIcon,
    CalendarIcon,
    ClockIcon,
    CrossIcon,
    CompanyIcon,
    KnifeForkIcon,
    TableIcon,
    TagIcon,
    TelIcon,
    TagLabels,
    ReceptionStatus,
  },
  props: {
    after: { type: Object, default: () => {} },
    before: { type: Object },
  },
  computed: {
    purposeMap: () => purposeMap,
    settlementMap: () => settlementMap,
    typeMap: () => typeMap,
    ...mapState({
      medias: state => state.medias,
    }),
    endTime() {
      const { hour, minute } = this.after.reserveDatetime
      return getEndTime(hour, minute, this.after.utilizationTime)
    },
    afterReserveName() {
      return this.reserveName(this.after)
    },
    beforeReserveName() {
      return this.reserveName(this.before)
    },
    afterRuby() {
      return this.ruby(this.after.customer)
    },
    beforeRuby() {
      if (!this.before) return null
      return this.ruby(this.before.customer)
    },
    isShowRuby() {
      const {
        customer: { lastName, firstName, lastNameKana, firstNameKana },
      } = this.after
      return (lastName || firstName) && (lastNameKana || firstNameKana)
    },
    isBlock() {
      return isBlock(this.after.type)
    },
  },
  methods: {
    isGnavi,
    formatDate,
    formatTime,
    couponPoint,
    couponPointNote,
    pointPayment,
    diff(target) {
      return this.before && !isEqual(get(this.before, target), get(this.after, target))
    },
    compareToCourse(target) {
      if (!this.before) return true
      const rsvCourse = this.before.courses && this.before.courses.find(obj => obj.id === target.id)
      return rsvCourse && isEqual(rsvCourse.count, target.count)
    },
    reserveName(reserve) {
      return reserveName({ ...reserve, reserveName: null })
    },
    ruby({ lastNameKana, firstNameKana }) {
      return (lastNameKana && firstNameKana && `${lastNameKana} ${firstNameKana}`) || lastNameKana || firstNameKana
    },
    tel({ tel, tel1, tel2 }) {
      tel = tel || tel1
      return tel && tel2 ? `${tel} / ${tel2}` : tel || tel2
    },
    mail({ mail1, mail2 }) {
      return mail1 && mail2 ? `${mail1} / ${mail2}` : mail1 || mail2
    },
  },
}
</script>

<style lang="postcss" scoped>
.bar {
  align-items: center;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: auto 1fr auto;
}

.created-at {
  line-height: 1;
  position: static;
  text-align: right;
  word-break: keep-all;

  .responsive & {
    @media screen and (--sp-size) {
      font-size: 11px;
    }
  }
}

.reserve-diff {
  background-color: var(--white);
  fill: var(--text-color);
  padding: 24px;

  .diff {
    color: var(--pure-red);
  }
  .responsive & {
    @media screen and (--sp-size) {
      padding: 16px;
    }
  }
}

.reserve-info {
  display: grid;
  font-size: 21px;
  grid-gap: 16px;
  grid-template-columns: auto 1fr;
  line-height: 1.2;
  margin-top: 16px;

  dt {
    height: 24px;
  }

  dd {
    align-items: center;
    margin-right: -8px;
  }

  span {
    margin-right: 24px;
  }

  .responsive & {
    @media screen and (--sp-size) {
      align-items: center;
      font-size: 16px;
      grid-gap: 8px;
      line-height: 1;

      dt {
        height: 16px;
      }

      span {
        margin-right: 12px;
      }

      svg {
        height: 16px;
        width: 16px;
      }
    }
  }
}

.time {
  display: flex;
  flex-wrap: wrap;

  .responsive & {
    @media screen and (--sp-size) {
      span {
        margin: 0;
      }
    }
  }
}

.name {
  border-top: 1px solid var(--border-color);
  display: block;
  margin-bottom: 22px;
  margin-top: 24px;
  padding-top: 24px;

  em {
    color: var(--text-color);
    font-size: 21px;
    font-style: normal;
    font-weight: bold;
    margin-right: 8px;
    white-space: normal !important;
  }

  .ruby {
    font-size: 13px;
  }

  .responsive & {
    @media screen and (--sp-size) {
      font-size: 13px;
      margin-bottom: 12px;
      margin-top: 16px;
      padding-top: 16px;
      em {
        font-size: 15px;
      }
      .ruby {
        font-size: 11px;
        margin-top: 0;
      }
    }
  }
}

.prop {
  display: grid;
  grid-gap: 0 8px;
  grid-template-columns: auto 1fr;

  dt {
    align-items: center;
    font-weight: 600;
  }

  dd {
    align-items: center;
  }

  .responsive & {
    @media screen and (--sp-size) {
      font-size: 13px;
      grid-gap: 8px;

      + .prop {
        margin-top: 8px;
      }

      dt {
        font-weight: 600;
        height: 16px;
        line-height: 1.4;
      }

      dd {
        align-self: center;
      }

      svg {
        height: 16px;
        width: 16px;
      }
    }
  }
}

.remarks {
  white-space: pre-line;
}

.tag-list {
  .responsive & {
    @media screen and (--sp-size) {
      display: block;
      margin-bottom: 20px;
      dt {
        height: auto;
        margin-bottom: 4px;
      }

      dd {
        height: auto;
      }

      + .prop {
        margin-top: 0;
      }

      >>> .tag {
        font-size: 13px;
      }
    }
  }
}

.course {
  align-items: center;
  display: flex;
  gap: 13px;

  span {
    font-size: 16px;
    line-height: 1.4;
  }

  .responsive & {
    @media screen and (max-width: 639px) {
      span {
        font-size: 13px;
      }
    }
  }
}

.settlement {
  dt {
    display: inline-block;
  }
  dd {
    display: inline;
  }

  .responsive & {
    display: block;
    @media screen and (--sp-size) {
      dt {
        height: auto;
        margin-right: 4px;
      }
      dd {
        line-height: 1.4;
      }
    }
  }
}

.coupon {
  display: block;

  span {
    font-weight: 400;
  }

  dd {
    white-space: pre-line;
  }

  .responsive & {
    @media screen and (--sp-size) {
      dt {
        height: auto;
      }

      dd {
        font-size: 11px;
        line-height: 16px;
      }
    }
  }
}
</style>
